import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/Tabs';
import { useRbacStore } from '../../../store/rbacStore';
import { TodayPanel } from './TodayPanel';
import { HistoryPanel } from './HistoryPanel';
import { AdjustmentsPanel } from './AdjustmentsPanel';
import { AttendanceReports } from './AttendanceReports';
import { TeamPanel } from './TeamPanel';
import { ApprovalsPanel } from './ApprovalsPanel';
import { SettingsPanel } from './SettingsPanel';

const WIDE = ['Team', 'Department', 'Company'];

/**
 * People › Attendance. Employees see Today / History / Adjustments; managers
 * and HR also get Team and Approvals, HR gets Settings. Tabs follow the same
 * role grants the API enforces (the API remains the authority).
 */
export function AttendanceCalendar() {
    useRbacStore((s) => s.permissions);
    const { hasPermission, getScope } = useRbacStore.getState();
    const [params, setParams] = useSearchParams();

    const tabs = [
        { id: 'today', label: 'Today', show: true },
        { id: 'history', label: 'History', show: true },
        { id: 'adjustments', label: 'My adjustments', show: true },
        { id: 'insights', label: 'Insights', show: hasPermission('Attendance', 'Reports', 'View') },
        { id: 'team', label: 'Team', show: WIDE.includes(getScope('Attendance', 'Dashboard', 'View') || '') },
        { id: 'approvals', label: 'Approvals', show: WIDE.includes(getScope('Attendance', 'Regularization', 'Approve') || '') },
        { id: 'settings', label: 'Settings', show: hasPermission('Attendance', 'Settings', 'View') },
    ].filter((t) => t.show);
    const requested = params.get('tab') || 'today';
    const active = tabs.some((t) => t.id === requested) ? requested : 'today';
    const go = (id: string) => setParams(id === 'today' ? {} : { tab: id }, { replace: true });

    return (
        <div className="space-y-6 p-6">
            <PageHeader className="mb-0" title="Attendance" description="Check in and out, see your hours, and fix mistakes." />
            <Tabs value={active} onValueChange={go}>
                <TabsList variant="underline" className="w-full overflow-x-auto">
                    {tabs.map((t) => <TabsTrigger key={t.id} variant="underline" value={t.id}>{t.label}</TabsTrigger>)}
                </TabsList>
                <TabsContent value="today" className="mt-4"><TodayPanel onShowAdjustments={() => go('adjustments')} /></TabsContent>
                <TabsContent value="history" className="mt-4"><HistoryPanel /></TabsContent>
                <TabsContent value="adjustments" className="mt-4"><AdjustmentsPanel /></TabsContent>
                <TabsContent value="insights" className="mt-4"><AttendanceReports /></TabsContent>
                <TabsContent value="team" className="mt-4"><TeamPanel canCorrect={WIDE.includes(getScope('Attendance', 'Regularization', 'Approve') || '')} /></TabsContent>
                <TabsContent value="approvals" className="mt-4"><ApprovalsPanel /></TabsContent>
                <TabsContent value="settings" className="mt-4"><SettingsPanel canEdit={hasPermission('Attendance', 'Settings', 'Edit')} /></TabsContent>
            </Tabs>
        </div>
    );
}
